import type { RunStatusSummary } from "@nova-ai/shared"

import { humanizeEnumLabel } from "../../lib/enum-label"
import { StatusBadge } from "../ui/status-badge"
import { Tooltip } from "../ui/tooltip"

type Readiness = RunStatusSummary["readiness"]
type ReadinessBlocker = Readiness["blockers"][number]

const PRECONDITION_DETAIL_BY_REASON: Record<string, string> = {
  no_category: "without a marketplace category",
  no_writer: "without an active writer for their category",
  no_catalog_row: "without source catalog data",
  no_facts: "without source facts",
}

export function RunReadinessBadge({ readiness }: { readiness: Readiness | undefined }) {
  if (readiness?.status !== "blocked" || readiness.blockers.length === 0) {
    return null
  }

  const blockers = readiness.blockers.map(readinessBlockerCopy)
  const accessibleLabel = `Blocked by: ${blockers.map(blockerAccessibleCopy).join("; ")}`

  return (
    <Tooltip content={<ReadinessBlockerTooltip blockers={blockers} />}>
      <button
        type="button"
        aria-label={accessibleLabel}
        className="group inline-flex cursor-help rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
      >
        <StatusBadge tone="destructive" className="transition-colors group-hover:bg-destructive/15">
          Blocked
        </StatusBadge>
      </button>
    </Tooltip>
  )
}

type BlockerCopy = ReturnType<typeof readinessBlockerCopy>

function ReadinessBlockerTooltip({ blockers }: { blockers: readonly BlockerCopy[] }) {
  return (
    <div className="space-y-2">
      <p className="font-semibold">Blocked by</p>

      <ul className="space-y-2">
        {blockers.map((blocker, index) => (
          <li key={`${blocker.title}:${index}`}>
            <p>{blocker.title}</p>

            {blocker.reasons.length > 0 && (
              <p className="text-popover-foreground/70">{blocker.reasons.join(" · ")}</p>
            )}

            {blocker.skuSample && (
              <p className="text-popover-foreground/70">
                Affected SKUs: <span className="font-mono">{blocker.skuSample}</span>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function readinessBlockerCopy(blocker: ReadinessBlocker) {
  return {
    title: blockerTitle(blocker),
    reasons: blockerReasons(blocker),
    skuSample: blockerSkuSample(blocker),
  }
}

function blockerAccessibleCopy(blocker: BlockerCopy) {
  return [
    blocker.title,
    blocker.reasons.join(", "),
    blocker.skuSample ? `Affected SKUs: ${blocker.skuSample}` : "",
  ]
    .filter(Boolean)
    .join(". ")
}

function blockerTitle(blocker: ReadinessBlocker) {
  const count = blocker.count.toLocaleString()
  switch (blocker.kind) {
    case "active_product_state":
      return `${count} ${plural(blocker.count, "product")} without current active-product data`
    case "preconditions": {
      const [onlyReason] = blocker.reasonCodes
      if (blocker.reasonCodes.length === 1 && onlyReason) {
        const detail = PRECONDITION_DETAIL_BY_REASON[onlyReason]
        if (detail) {
          return `${count} ${plural(blocker.count, "product")} ${detail}`
        }
      }
      return `${count} ${plural(blocker.count, "product")} missing preparation requirements`
    }
    case "validation":
      return `${count} validation ${plural(blocker.count, "gap")}`
    case "feed_build":
      return `${count} feed-build ${plural(blocker.count, "blocker")}`
    case "run_error":
      return "Run failed"
    default:
      return `${count} ${humanizeEnumLabel(blocker.kind).toLowerCase()} ${plural(blocker.count, "issue")}`
  }
}

function blockerReasons(blocker: ReadinessBlocker) {
  const [onlyReason] = blocker.reasonCodes
  if (blocker.kind === "active_product_state") {
    return []
  }
  if (
    blocker.kind === "preconditions" &&
    blocker.reasonCodes.length === 1 &&
    onlyReason &&
    PRECONDITION_DETAIL_BY_REASON[onlyReason]
  ) {
    return []
  }
  if (blocker.kind === "run_error") {
    return blocker.reasonCodes
  }
  return blocker.reasonCodes.map((reasonCode) => {
    if (reasonCode.startsWith("no_")) {
      return `No ${humanizeEnumLabel(reasonCode.slice(3)).toLowerCase()}`
    }
    return humanizeEnumLabel(reasonCode.replaceAll(":", "_"))
  })
}

function blockerSkuSample(blocker: ReadinessBlocker) {
  const visibleSkus = blocker.sampleSkus.slice(0, 3)
  if (visibleSkus.length === 0) {
    return null
  }
  const remainingCount = Math.max(0, blocker.count - visibleSkus.length)
  return `${visibleSkus.join(", ")}${remainingCount > 0 ? ` +${remainingCount.toLocaleString()} more` : ""}`
}

function plural(count: number, singular: string, pluralForm = `${singular}s`) {
  return count === 1 ? singular : pluralForm
}
